import { useState, useEffect, useCallback, useRef } from 'react';
import * as DocumentUploadService from '../../services/documentUploadService';
import './attachmentManager.scss';

const AttachmentManager = (props) => {
  const maxDocSizeKey = 'maxDocSize';
  const dropRef = useRef(null);
  const inputFileRef = useRef(null);
  const filesInfoKey = 'filesInfo';
  const progressInfoKey = 'progressInfo';
  const attachmentsData = props.control.getAttachmentsInfo();
  const [docSize, updateMaxDocSize] = useState(() => {
    const size = attachmentsData[maxDocSizeKey];
    return { maxDocSize: size };
  });

  const [referralUniqueId, setReferralIdentifier] = useState(() => {
    const guid = attachmentsData[DocumentUploadService.referralGuidKey];
    return { referralGuid: guid };
  });

  const [filesState, setFilesState] = useState(() => {
    const state = attachmentsData[filesInfoKey];
    if (!state) {
      props.control.onAttachmentChangeHandler(filesInfoKey, []);
      return [];
    }
    return state;
  });

  const [progressState, setProgressState] = useState(() => {
    const progressInfo = attachmentsData[progressInfoKey];
    if (!progressInfo) {
      props.control.onAttachmentChangeHandler(progressInfoKey, []);
      return [];
    }
    return progressInfo;
  });

  const [uploadFilesInProgress, setUploadingFiles] = useState(() => {
    return false;
  });

  const [uploadNextId, setUploadNextId] = useState(() => {
    const state = attachmentsData[filesInfoKey];
    let nextId = 0;
    if (!state) {
      nextId = Math.max(...state.map(f => f.id)) + 1;
    }
    return nextId;
  });

  const updateFilesState = useCallback(files => {
    props.control.onAttachmentChangeHandler(filesInfoKey, files);
    setFilesState(() => {
      return files;
    });
  }, [props]);

  const updateProgressState = useCallback(progressList => {
    props.control.onAttachmentChangeHandler(progressInfoKey, progressList);
    setProgressState(() => {
      return progressList;
    });
  }, [props]);

  const updateOneProgressState = useCallback((progressInfos, progressInfo, index) => {
    setProgressState(() => {
      return [
        ...progressInfos.slice(0, index),
        {
          ...progressInfo,
        },
        ...progressInfos.slice(index + 1)
      ];
    });
  }, []);

  const uploadFiles = useCallback((fileList, filesUpdate, progressInfoUpdate, referralUniqueId) => {
    const progressInfos = progressInfoUpdate;
    if (referralUniqueId.referralGuid !== undefined) {
      fileList.forEach(fileInfo => {
        const file = filesUpdate.find(f => f.id === fileInfo.id);
        const index = progressInfos.findIndex(f => f.id === fileInfo.id);
        if (file && index >= 0) {
          const progressInfo = progressInfos[index];
          progressInfo.isWaiting = false;
          progressInfo.inProgress = true;
          updateOneProgressState(progressInfos, progressInfo, index);

          props.control.setBusy(true);
          DocumentUploadService.upload(referralUniqueId.referralGuid, file, (e) => {
            progressInfo.progress = Math.round((100 * e.loaded) / e.total);
            updateOneProgressState(progressInfos, progressInfo, index);
          })
            .then(({ data }) => {
              progressInfo.progress = 100;
              progressInfo.referenceId = data;
            })
            .catch(() => {
              progressInfo.hasError = true;
            })
            .finally(() => {
              props.control.setBusy(false);
              progressInfo.inProgress = false;
              updateOneProgressState(progressInfos, progressInfo, index);
              updateProgressState(progressInfos);
              const info = progressInfos.find((f) => f.isWaiting === true);
              if (!info) {
                setUploadingFiles(() => {
                  return false;
                });
              }
            });
        }
      });

      updateProgressState(progressInfos);
    }
  }, [updateProgressState, updateOneProgressState, setUploadingFiles, props]);

  const handleDropFiles = useCallback((files) => {
    const fileList = [];
    const progressList = [];
    let nextId = uploadNextId;
    for (var i = 0; i < files.length; i++) {
      if (!files[i].name) return;
      fileList.push({ id: nextId, file: files[i] });
      progressList.push({
        id: nextId,
        progress: 0,
        inProgress: false,
        isWaiting: true,
        hasError: false
      });
      nextId++;
    }

    setUploadNextId(() => {
      return nextId;
    });

    const filesUpdate = [...filesState, ...fileList];
    updateFilesState(filesUpdate);

    const progressInfoUpdate = [...progressState, ...progressList];
    updateProgressState(progressInfoUpdate);

    if (referralUniqueId.referralGuid === undefined) {
      DocumentUploadService.getUniqueId()
        .then(({ data }) => {
          props.control.onAttachmentChangeHandler(DocumentUploadService.referralGuidKey, data);
          setReferralIdentifier({ referralGuid: data });
          referralUniqueId.referralGuid = data;
          return true;
        })
        .then(() => {
          uploadFiles(fileList, filesUpdate, progressInfoUpdate, referralUniqueId);
          return true;
        })
        .catch(() => {
          for (var i = 0; i < progressList.length; i++) {
            const item = progressList[i];
            const progressInfo = progressInfoUpdate.find((f) => f.id === item.id);
            if (progressInfo) {
              progressInfo.hasError = true;
              progressInfo.isWaiting = false;
            }
          }
          updateProgressState(progressInfoUpdate);
          setUploadingFiles(() => {
            return false;
          });
        });
    } else {
      uploadFiles(fileList, filesUpdate, progressInfoUpdate, referralUniqueId);
    }
  }, [filesState, updateFilesState, progressState, updateProgressState, uploadFiles, setUploadingFiles, referralUniqueId, props, uploadNextId, setUploadNextId]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadFilesInProgress) {
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        setUploadingFiles(() => {
          return true;
        });
        handleDropFiles(e.dataTransfer.files);
      }
    }
    e.dataTransfer.clearData();
  }, [handleDropFiles, uploadFilesInProgress, setUploadingFiles]);

  useEffect(() => {
    const div = dropRef.current;
    div.addEventListener('dragenter', handleDrag);
    div.addEventListener('dragleave', handleDrag);
    div.addEventListener('dragover', handleDrag);
    div.addEventListener('drop', handleDrop);

    if (!docSize?.maxDocSize) {
      DocumentUploadService.getMaxUploadSizeInMB().then(({ data }) => {
        props.control.onAttachmentChangeHandler(maxDocSizeKey, data);
        updateMaxDocSize({ maxDocSize: data });
      });
    }

    return () => {
      div.removeEventListener('dragenter', handleDrag);
      div.removeEventListener('dragleave', handleDrag);
      div.removeEventListener('dragover', handleDrag);
      div.removeEventListener('drop', handleDrop);
    };
  }, [props, dropRef, handleDrag, handleDrop, docSize]);

  const handleFileUpload = e => {
    const { files } = e.target;
    if (files && files.length) {
      handleDropFiles(files);
    }
  };

  const onAddFilesClick = () => {
    inputFileRef.current.click();
  };

  const handleDelete = (id) => {
    if (!uploadFilesInProgress) {
      const progressInfo = progressState.find((f) => f.id === id);

      if (!progressInfo || !progressInfo.inProgress) {
        if (progressInfo && referralUniqueId.referralGuid) {
          DocumentUploadService.deleteFile(referralUniqueId.referralGuid, progressInfo.referenceId)
            .then(() => {
              removeFileFromState(id);
              return true;
            });
        } else {
          removeFileFromState(id);
        }
      } else {
        removeFileFromState(id);
      }
    }
  };

  const removeFileFromState = (id) => {
    const files = filesState.filter(f => f.id !== id);
    updateFilesState(files);
    const progressInfos = progressState.filter(f => f.id !== id);
    updateProgressState(progressInfos);
  };

  const showFileInfo = (item) => {
    const progressInfo = progressState.find(p => p.id === item.id);
    const isWaiting = progressInfo?.isWaiting ?? false;
    const hasError = progressInfo?.hasError ?? false;
    const progress = progressInfo?.progress ?? 0;

    return (<div className={hasError ? 'fit error' : 'fit'}>
      <span>{hasError ? 'There was an error uploading: ' + item.file.name : item.file.name}</span>
      <div className={isWaiting ? 'wait' : ''} style={
        {
          height: '2px', width: isWaiting ? '100%' : progress + '%',
          backgroundColor: progress === 0 ? isWaiting ? 'darkgray' : 'white' : hasError ? 'red' : 'green'
        }}>
      </div>
    </div>);
  };

  return (
    <div>
      <label className="attachement-title">Max file size is {docSize?.maxDocSize ?? 2} MB.</label>
      <div className="document-upload">
        <div key="files" className="files-list">
          {filesState.map((item, i) => {
            return (
              <div title={item.file.name} className={i % 2 === 1 ? 'file even' : 'file odd'} key={item.id}>
                <div><i className="icon fa fa-file"></i></div>
                <div className="info">
                  {showFileInfo(item)}
                </div>
                <span className="fa-stack circle" onClick={() => handleDelete(item.id)}>
                  <i className="fa fa-circle fa-stack-2x remove"></i>
                  <i className="fa fa-close fa-stack-1x"></i>
                </span>
              </div>
            );
          })}
        </div>
        <div key="drop-area" ref={dropRef} className="drop-area">
          <div key="legend" className="legend" onClick={onAddFilesClick}>
            <span>Either drag selected files here or click to select the file(s) you wish to upload.</span>
            <input ref={inputFileRef} type="file" style={{ display: 'none' }} multiple onChange={handleFileUpload}></input>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentManager;
